import { useState, useEffect, useRef, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { getTotals, getEntries, formatAmount } from '../lib/api';
import { supabase } from '../lib/supabase';
import { Badge } from '../components/ui/badge';
import { useNavigate } from 'react-router-dom';

// ─── Period helpers ──────────────────────────────────────────────────────────

function pad(n) { return String(n).padStart(2, '0'); }
function toDateStr(d) { return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`; }

function getTodayRange()  {
  const t = toDateStr(new Date());
  return { label: 'Today', dateFrom: t, dateTo: t };
}
function getWeekRange() {
  const now = new Date();
  const day = now.getDay(); // 0=Sun
  const mon = new Date(now); mon.setDate(now.getDate() - ((day + 6) % 7));
  return { label: 'This Week', dateFrom: toDateStr(mon), dateTo: toDateStr(now) };
}
function getMonthRange() {
  const now = new Date();
  const first = new Date(now.getFullYear(), now.getMonth(), 1);
  return { label: 'This Month', dateFrom: toDateStr(first), dateTo: toDateStr(now) };
}
function getYearRange() {
  const y = new Date().getFullYear();
  return { label: 'This Year', dateFrom: `${y}-01-01`, dateTo: `${y}-12-31` };
}
function getFYRange() {
  const now = new Date();
  const m = now.getMonth(); // 0-indexed
  const y = now.getFullYear();
  const fyStartY = m >= 3 ? y : y - 1; // FY starts April (month 3)
  const label = `FY ${fyStartY}-${String(fyStartY + 1).slice(2)}`;
  return { label, dateFrom: `${fyStartY}-04-01`, dateTo: `${fyStartY + 1}-03-31` };
}

function formatPeriodLabel({ label, dateFrom, dateTo }) {
  if (label !== 'Custom') return label;
  const fmt = (s) => {
    if (!s) return '';
    const [, m, d] = s.split('-');
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    return `${d} ${months[parseInt(m, 10) - 1]}`;
  };
  return `${fmt(dateFrom)} – ${fmt(dateTo)}`;
}

// ─── Component ───────────────────────────────────────────────────────────────

const DashboardPage = () => {
  const { userProfile, business } = useAuth();
  const navigate = useNavigate();

  const [totals, setTotals]           = useState({ sales: 0, payments: 0, trips: 0, due: 0 });
  const [recentEntries, setRecentEntries] = useState([]);
  const [loading, setLoading]         = useState(true);
  const [period, setPeriod]           = useState(getTodayRange);
  const [showPicker, setShowPicker]   = useState(false);
  const [customFrom, setCustomFrom]   = useState('');
  const [customTo, setCustomTo]       = useState('');
  const pickerRef = useRef(null);

  // Close picker on outside click
  useEffect(() => {
    if (!showPicker) return;
    const handler = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
      // Give browser-native UI (e.g. date picker popups) time to initialise
      // before the outside-click detection fires — use 'click' not 'mousedown'.
      document.addEventListener('click', handler);
      return () => document.removeEventListener('click', handler);
  }, [showPicker]);

  const loadDashboardData = useCallback(async (p) => {
    if (!business?.id) return;
    try {
      setLoading(true);
      const [totalsRes, entriesRes] = await Promise.all([
        getTotals(business.id, p.dateFrom, p.dateTo),
        getEntries(business.id, { limit: 5 }),
      ]);
      setTotals(totalsRes.data);
      setRecentEntries(entriesRes.data);
    } catch (err) {
      console.error('Dashboard load error:', err);
      // Keep zeros shown — do not crash the page
    } finally {
      setLoading(false);
      if (process.env.NODE_ENV === 'development') {
        console.log('[Dashboard] business.id:', business?.id,
                    'period:', p.dateFrom, '-', p.dateTo);
      }
    }
  }, [business]);

  // Load when business or period changes
  useEffect(() => {
    if (business?.id) {
      loadDashboardData(period);
    }
  }, [business, period, loadDashboardData]);

  // Realtime + 5s polling fallback (mirrors ChatPage pattern). Required for
  // RLS-on environments where the anon supabase client has no authenticated
  // session and the channel errors out — we silently fall back to polling.
  useEffect(() => {
    if (!business?.id) return;
    let pollTimer = null;

    const startPoll = () => {
      if (pollTimer) return;
      pollTimer = setInterval(() => loadDashboardData(period), 5000);
    };
    const stopPoll = () => {
      if (pollTimer) { clearInterval(pollTimer); pollTimer = null; }
    };

    const channel = supabase
      .channel('entries-changes')
      .on('postgres_changes', { event: '*', schema: 'public', table: 'entries',
          filter: `business_id=eq.${business.id}` },
        () => loadDashboardData(period))
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          stopPoll();
        } else if (status === 'CHANNEL_ERROR' || status === 'TIMED_OUT' || status === 'CLOSED') {
          startPoll();
        }
      });

    return () => {
      stopPoll();
      try { supabase.removeChannel(channel); }
      catch (err) { console.warn('[Dashboard] removeChannel cleanup failed:', err); }
    };
  }, [business, period, loadDashboardData]);

  const applyPeriod = (p) => {
    setPeriod(p);
    setShowPicker(false);
  };

  const applyCustom = () => {
    if (!customFrom || !customTo) return;
    applyPeriod({ label: 'Custom', dateFrom: customFrom, dateTo: customTo });
  };

  // Quick presets list
  const PRESETS = [getTodayRange(), getWeekRange(), getMonthRange(), getYearRange(), getFYRange()];

  // ── Sub-components ──────────────────────────────────────────────────────────

  const StatCard = ({ title, value, icon, color, onClick }) => (
    <div className="rounded-2xl hover:opacity-90 transition-opacity cursor-pointer"
      style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}
      onClick={onClick}
      data-testid={`stat-card-${title.toLowerCase().replace(/\s+/g, '-')}`}>
      <div className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-xs mb-1" style={{ color: '#8E8E93' }}>{title}</p>
            <p className={`text-2xl font-bold ${color}`}>{value}</p>
          </div>
          <div className={`w-10 h-10 rounded-full flex items-center justify-center opacity-20 ${color.replace('text-', 'bg-')}`}>
            {icon}
          </div>
        </div>
      </div>
    </div>
  );

  const QuickAction = ({ icon, label, onClick }) => (
    <button
      className="flex-1 h-auto py-4 flex flex-col items-center gap-2 rounded-2xl transition-opacity hover:opacity-80"
      style={{ border: '1px solid #2C2C2E', color: '#F5F5F7', background: '#1C1C1E' }}
      onClick={onClick}
      data-testid={`quick-action-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      {icon}
      <span className="text-xs">{label}</span>
    </button>
  );

  const getStatusBadge = (status) => {
    const styles = {
      confirmed: 'bg-emerald-500/20 text-emerald-400',
      corroborated: 'bg-blue-500/20 text-blue-400',
      disputed: 'bg-amber-500/20 text-amber-400',
      voided: 'bg-red-500/20 text-red-400 line-through',
    };
    return <Badge className={styles[status] || styles.confirmed}>{status}</Badge>;
  };

  const periodDisplay = formatPeriodLabel(period);

  // ── Render ──────────────────────────────────────────────────────────────────

  return (
    <div className="min-h-screen text-white" data-testid="dashboard-home"
      style={{ background: '#000', fontFamily: "-apple-system, BlinkMacSystemFont, 'SF Pro Display', system-ui, sans-serif" }}>

      {/* ── Header ── */}
      <header className="sticky top-0 z-20" ref={pickerRef}
        style={{ background: 'rgba(0,0,0,.90)', backdropFilter: 'saturate(180%) blur(20px)', borderBottom: '1px solid #1C1C1E' }}>
        <div className="p-4 flex items-center justify-between">
          {/* Left: business info */}
          <div>
            <h1 className="text-xl font-bold">{business?.name || 'Noteho'}</h1>
            <p className="text-sm" style={{ color: '#8E8E93' }}>{userProfile?.name || 'Welcome'}</p>
          </div>

      {/* Right: calendar + settings */}
          <div className="flex items-center gap-1">
            {/* Calendar button */}
            <button
              onClick={() => setShowPicker(p => !p)}
              className="flex flex-col items-center justify-center rounded-xl px-2 py-1 transition-colors"
              style={{
                background: showPicker ? '#1C1C1E' : 'transparent',
                border: `1px solid ${showPicker ? '#3A3A3C' : 'transparent'}`,
                minWidth: 52,
              }}
              data-testid="period-picker-btn">
              <svg className="w-5 h-5" fill="none" stroke={showPicker ? '#FF2D55' : '#8E8E93'} viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.8}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
              </svg>
              <span className="text-xs mt-0.5 font-medium leading-none"
                style={{ color: showPicker ? '#FF2D55' : '#636366', fontSize: 9 }}>
                {periodDisplay.length > 10 ? period.label.replace('This ', '').replace('Financial Year', 'FY') : periodDisplay}
              </span>
            </button>

            {/* Settings */}
            <button
              onClick={() => navigate('/settings')}
              className="w-9 h-9 flex items-center justify-center rounded-xl transition-colors hover:bg-white/5"
              data-testid="settings-btn">
              <svg className="w-5 h-5" fill="none" stroke="#8E8E93" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
                  d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
              </svg>
            </button>
          </div>
        </div>

        {/* ── Preset chips — ALWAYS VISIBLE ── */}
        <div className="px-4 pb-3 flex flex-wrap gap-2" data-testid="period-picker-panel">
          {PRESETS.map((p) => {
            const active = period.label === p.label;
            return (
              <button key={p.label}
                onClick={() => applyPeriod(p)}
                className="px-3 py-1.5 rounded-full text-xs font-medium transition-all"
                style={{
                  background: active ? '#FF2D55' : '#2C2C2E',
                  color: active ? '#fff' : '#8E8E93',
                  border: `1px solid ${active ? '#FF2D55' : '#3A3A3C'}`,
                }}
                data-testid={`preset-${p.label.toLowerCase().replace(/\s+/g, '-')}`}>
                {p.label}
              </button>
            );
          })}
        </div>

        {/* ── Custom date range — only when picker (calendar icon) is open ── */}
        {showPicker && (
          <div className="px-4 pb-4" data-testid="period-custom-range"
            style={{ borderTop: '1px solid #1C1C1E' }}>
            <div className="flex items-center gap-2 pt-3">
              <input
                type="date"
                value={customFrom}
                onChange={e => setCustomFrom(e.target.value)}
                max={customTo || undefined}
                className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
                style={{ background: '#2C2C2E', color: '#fff', border: '1px solid #3A3A3C' }}
                data-testid="custom-from-input"
              />
              <span style={{ color: '#636366', flexShrink: 0 }}>–</span>
              <input
                type="date"
                value={customTo}
                onChange={e => setCustomTo(e.target.value)}
                min={customFrom || undefined}
                className="flex-1 px-3 py-2 rounded-xl text-sm outline-none"
                style={{ background: '#2C2C2E', color: '#fff', border: '1px solid #3A3A3C' }}
                data-testid="custom-to-input"
              />
              <button
                onClick={applyCustom}
                disabled={!customFrom || !customTo}
                className="px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-30"
                style={{ background: '#FF2D55', color: '#fff', flexShrink: 0 }}
                data-testid="apply-custom-btn">
                Apply
              </button>
            </div>
          </div>
        )}
      </header>

      {/* ── Main content ── */}
      <main className="p-4 space-y-6">

        {/* Period label strip */}
        <div className="flex items-center justify-between">
          <p className="text-sm font-medium" style={{ color: '#636366' }}>
            {periodDisplay}
          </p>
          {loading && (
            <div className="w-4 h-4 animate-spin rounded-full border-b-2"
              style={{ borderColor: 'transparent transparent #FF2D55 transparent' }} />
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-2 gap-4" data-testid="stats-grid">
          <StatCard
            title="Sales"
            value={formatAmount(totals.sales)}
            color="text-emerald-400"
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            onClick={() => navigate('/daybook')}
          />
          <StatCard
            title="Received"
            value={formatAmount(totals.payments)}
            color="text-blue-400"
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" /></svg>}
            onClick={() => navigate('/daybook')}
          />
          <StatCard
            title="Trips"
            value={totals.trips}
            color="text-amber-400"
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>}
            onClick={() => navigate('/daybook')}
          />
          <StatCard
            title="Due"
            value={formatAmount(totals.due || Math.max(0, totals.sales - totals.payments))}
            color="text-red-400"
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" /></svg>}
            onClick={() => navigate('/daybook')}
          />
        </div>

        {/* Quick Actions */}
        <div className="flex gap-3" data-testid="quick-actions">
          <QuickAction
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4" /></svg>}
            label="New Trip"
            onClick={() => navigate('/chat')}
          />
          <QuickAction
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" /></svg>}
            label="Invoice"
            onClick={() => navigate('/chat')}
          />
          <QuickAction
            icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
            label="Report"
            onClick={() => navigate('/reports')}
          />
        </div>

        {/* Recent Entries */}
        <div className="rounded-2xl overflow-hidden" style={{ background: '#1C1C1E', border: '1px solid #2C2C2E' }}>
          <div className="px-4 pt-4 pb-2 flex items-center justify-between">
            <h2 className="text-base font-semibold text-white">Recent Entries</h2>
            <button className="text-sm transition-opacity hover:opacity-70" style={{ color: '#FF2D55' }}
              onClick={() => navigate('/daybook')}>
              View All
            </button>
          </div>
          <div className="px-4 pb-4 space-y-2" data-testid="recent-entries">
            {recentEntries.length === 0 ? (
              <p className="text-center py-4 text-sm" style={{ color: '#636366' }}>No entries yet</p>
            ) : (
              recentEntries.map((entry) => (
                <div key={entry.id}
                  className={`p-3 rounded-xl ${entry.status === 'voided' ? 'opacity-50' : ''}`}
                  style={{ background: '#2C2C2E' }}
                  data-testid={`entry-${entry.id}`}>
                  <div className="flex items-center justify-between">
                    <div>
                      <p className={`font-medium text-sm ${entry.status === 'voided' ? 'line-through' : 'text-white'}`}
                        style={entry.status === 'voided' ? { color: '#636366' } : {}}>
                        {entry.party_name || 'Unknown'}
                      </p>
                      <p className="text-xs mt-0.5" style={{ color: '#8E8E93' }}>
                        {entry.item_name && `${entry.quantity || ''} ${entry.unit || ''} ${entry.item_name}`}
                        {entry.entry_type && ` • ${entry.entry_type}`}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className={`font-bold text-sm ${entry.status === 'voided' ? 'line-through' : 'text-emerald-400'}`}
                        style={entry.status === 'voided' ? { color: '#636366' } : {}}>
                        {formatAmount(entry.total_amount)}
                      </p>
                      {getStatusBadge(entry.status)}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </main>

      {/* Floating Chat Button */}
      <button
        onClick={() => navigate('/chat')}
        className="fixed bottom-20 right-4 z-50 w-14 h-14 rounded-full shadow-lg flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        style={{ background: 'linear-gradient(135deg, #FF2D55, #5856D6)' }}
        data-testid="nav-chat">
        <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2}
            d="M8 10h.01M12 10h.01M16 10h.01M9 16H5a2 2 0 01-2-2V6a2 2 0 012-2h14a2 2 0 012 2v8a2 2 0 01-2 2h-5l-5 5v-5z" />
        </svg>
      </button>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 px-4 py-2" data-testid="bottom-nav"
        style={{ background: '#1C1C1E', borderTop: '1px solid #2C2C2E' }}>
        <div className="flex justify-around">
          {[
            { icon: 'M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6', label: 'Home', path: '/dashboard', active: true },
            { icon: 'M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2', label: 'Day Book', path: '/daybook' },
            { icon: 'M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z', label: 'Contacts', path: '/contacts' },
            { icon: 'M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z', label: 'Admin', path: '/admin' },
            { icon: 'M9 3H5a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2V5a2 2 0 00-2-2h-4M9 3a1 1 0 000 2h6a1 1 0 000-2M9 3a1 1 0 011-1h4a1 1 0 011 1m-5 9l2 2 4-4', label: 'Live Log', path: '/live-log', live: true },
          ].map((item) => (
            <button key={item.label}
              onClick={() => navigate(item.path)}
              className="flex flex-col items-center p-2 relative"
              style={{ color: item.active ? '#FF2D55' : '#636366' }}
              data-testid={`nav-${item.label.toLowerCase().replace(' ', '-')}`}>
              <div className="relative">
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d={item.icon} />
                </svg>
                {item.live && <span className="absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full bg-emerald-400 animate-pulse" />}
              </div>
              <span className="text-xs mt-1">{item.label}</span>
            </button>
          ))}
        </div>
      </nav>
    </div>
  );
};

export default DashboardPage;
